import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Button, LinearProgress, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { quiz, courses } from '../../services/api';
import { QuizResults as QuizResultsType } from '../../types';

const ResultsContainer = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  padding: theme.spacing(4),
  minHeight: '100vh',
  backgroundColor: '#000000',
  color: 'white',
}));

const ResultCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '20px',
  marginBottom: theme.spacing(3),
}));

const ScoreDisplay = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
}));

const ScoreCircle = styled(Box)<{ passed: boolean }>(({ theme, passed }) => ({
  width: '120px',
  height: '120px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  backgroundColor: passed ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
  border: `3px solid ${passed ? '#4CAF50' : '#F44336'}`,
  marginBottom: theme.spacing(2),
}));

const QuizResults: React.FC = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState<QuizResultsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseInfo, setCourseInfo] = useState<any>(null);
  const [flowName, setFlowName] = useState<string | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        
        console.log('🎯 QuizResults: Initializing with:', {
          flowId,
          hasLocationState: !!location.state,
          locationState: location.state
        });
        
        // Check if results were passed via navigation state
        if (location.state?.quizResults) {
          console.log('✅ QuizResults: Using results from navigation state:', location.state.quizResults);
          setResults(location.state.quizResults);
          
          // Also check for course info when using navigation state
          const currentFlowName = location.state?.flowName;
          if (currentFlowName) {
            setFlowName(currentFlowName);
            console.log('🔍 QuizResults: Checking course info for flow (from nav state):', currentFlowName);
            
            try {
              const courseResponse = await courses.getFlowByName(currentFlowName);
              const courseData = courseResponse.data;
              
              if (courseData.course) {
                console.log('📚 QuizResults: Quiz is part of course:', courseData.course);
                setCourseInfo({
                  course: courseData.course,
                  flow: courseData.flow
                });
              } else {
                console.log('📝 QuizResults: Quiz is standalone (not part of course)');
              }
            } catch (courseError) {
              console.log('ℹ️ QuizResults: Could not fetch course info:', courseError);
            }
          }
          
          setLoading(false);
          return;
        }
        
        // Otherwise fetch from API
        if (flowId) {
          console.log('📡 QuizResults: Fetching results from API for flowId:', flowId);
          const response = await quiz.getResults(parseInt(flowId));
          console.log('✅ QuizResults: API response:', response.data);
          console.log('🔍 QuizResults: Response structure check:', {
            hasAttempt: !!response.data.attempt,
            attemptData: response.data.attempt,
            hasMessage: !!response.data.message,
            message: response.data.message,
            fullResponse: response.data
          });
          setResults(response.data);
          
          // Check if this quiz flow is part of a course
          try {
            // First get the flow name from the results or fetch it separately
            let currentFlowName = location.state?.flowName;
            
            if (!currentFlowName && response.data.attempt) {
              // Try to get flow name from the attempt data if available
              currentFlowName = response.data.attempt.flow_name;
            }
            
            if (currentFlowName) {
              setFlowName(currentFlowName);
              console.log('🔍 QuizResults: Checking course info for flow:', currentFlowName);
              
              const courseResponse = await courses.getFlowByName(currentFlowName);
              const courseData = courseResponse.data;
              
              if (courseData.course) {
                console.log('📚 QuizResults: Quiz is part of course:', courseData.course);
                setCourseInfo({
                  course: courseData.course,
                  flow: courseData.flow
                });
              } else {
                console.log('📝 QuizResults: Quiz is standalone (not part of course)');
              }
            }
          } catch (courseError) {
            console.log('ℹ️ QuizResults: Could not fetch course info:', courseError);
            // This is not a critical error, quiz results can still be shown
          }
        } else {
          console.error('❌ QuizResults: No flowId provided and no navigation state');
          setError('No quiz results found');
        }
      } catch (err) {
        console.error('❌ QuizResults: Error fetching quiz results:', err);
        setError('Failed to load quiz results');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [flowId, location.state]);

  const handleRetakeQuiz = () => {
    const flowName = location.state?.flowName || 'mock_quiz_test';
    navigate(`/flows/${flowName}`);
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  if (loading) {
    return (
      <ResultsContainer>
        <Typography variant="h4" gutterBottom>Loading Results...</Typography>
        <LinearProgress />
      </ResultsContainer>
    );
  }

  if (error || !results) {
    return (
      <ResultsContainer>
        <Typography variant="h4" gutterBottom color="error">
          {error || 'No results found'}
        </Typography>
        <Button variant="contained" onClick={handleBackToHome}>
          Back to Home
        </Button>
      </ResultsContainer>
    );
  }

  // Handle case where there are no completed attempts
  if (!results.attempt || results.message) {
    const isNoAttempts = results.message === 'No quiz attempts found';
    
    return (
      <ResultsContainer>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
          Quiz Results
        </Typography>
        
        <ResultCard>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom color="warning.main">
              {isNoAttempts ? '🎯 No Quiz Attempts' : '⏳ No Completed Attempts'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {isNoAttempts 
                ? 'You haven\'t started this quiz yet.' 
                : 'You have started this quiz but haven\'t completed any attempts yet.'}
            </Typography>
            
            {results.attempts && results.attempts.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You have {results.attempts.length} incomplete attempt(s).
              </Typography>
            )}
            
            <Box display="flex" gap={2} justifyContent="center">
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => {
                  // Navigate back to the quiz to continue/restart
                  if (flowId) {
                    navigate(`/flows/${flowId}`);
                  }
                }}
              >
                {isNoAttempts ? 'Start Quiz' : 'Continue Quiz'}
              </Button>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={handleBackToHome}
              >
                Back to Home
              </Button>
            </Box>
          </CardContent>
        </ResultCard>
      </ResultsContainer>
    );
  }

  const scorePercentage = Math.round(results.score_percentage || 0);
  const passed = results.passed || false;

  return (
    <ResultsContainer>
      <Typography variant="h3" gutterBottom align="center" sx={{ mb: 2 }}>
        Quiz Results
      </Typography>
      
      {courseInfo && (
        <Typography 
          variant="subtitle1" 
          align="center" 
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          📚 Part of: <strong>{courseInfo.course.name}</strong>
        </Typography>
      )}

      <ResultCard>
        <CardContent>
          <ScoreDisplay>
            <ScoreCircle passed={passed}>
              <Typography variant="h3" fontWeight="bold">
                {scorePercentage}%
              </Typography>
            </ScoreCircle>
            
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
              {passed ? (
                <CheckCircleIcon color="success" fontSize="large" />
              ) : (
                <CancelIcon color="error" fontSize="large" />
              )}
              <Typography variant="h5">
                {passed ? 'Passed!' : 'Not Passed'}
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary">
              You answered {results.correct_answers} out of {results.total_questions} questions correctly
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Passing score: {Math.round(results.passing_score || 70)}%
            </Typography>
            
            {courseInfo && passed && (
              <Typography 
                variant="body2" 
                color="success.main" 
                sx={{ mt: 2, fontWeight: 'bold' }}
              >
                ✅ Quiz passed! You can continue with the course.
              </Typography>
            )}
          </ScoreDisplay>
        </CardContent>
      </ResultCard>

      <ResultCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Attempt Summary
          </Typography>
          
          <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
            <Chip 
              label={`Attempt ${results.attempt?.attempt_number || 'Unknown'}`}
              color="primary"
            />
            <Chip 
              label={`${results.attempts_used || 0} of ${results.max_attempts || '∞'} attempts used`}
              variant="outlined"
            />
          </Box>
          
          {results.attempt?.started_at && (
            <Typography variant="body2" color="text.secondary">
              Started: {new Date(results.attempt.started_at).toLocaleString()}
            </Typography>
          )}
          {results.attempt?.completed_at && (
            <Typography variant="body2" color="text.secondary">
              Completed: {new Date(results.attempt.completed_at).toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </ResultCard>

      {/* Individual Question Results */}
      {results.question_results && results.question_results.length > 0 && (
        <ResultCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Question Results
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              {results.question_results.map((questionResult, index) => (
                <Box 
                  key={questionResult.question_id}
                  sx={{ 
                    mb: 3, 
                    p: 2, 
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    backgroundColor: questionResult.is_correct 
                      ? 'rgba(76, 175, 80, 0.1)' 
                      : 'rgba(244, 67, 54, 0.1)'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {questionResult.is_correct ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="subtitle2" fontWeight="bold">
                      Question {index + 1}
                    </Typography>
                    <Chip 
                      label={questionResult.is_correct ? 'Correct' : 'Incorrect'}
                      color={questionResult.is_correct ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>
                    {questionResult.question_text}
                  </Typography>
                  
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Your Answer:</strong> {questionResult.user_answer}
                    </Typography>
                    {!questionResult.is_correct && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                        <strong>Correct Answer:</strong> {questionResult.correct_answer}
                      </Typography>
                    )}
                    {questionResult.explanation && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        <strong>Explanation:</strong> {questionResult.explanation}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </ResultCard>
      )}

      <Box display="flex" gap={2} justifyContent="center" mt={4} flexWrap="wrap">
        {/* Show Continue Course button if quiz is passed and part of a course */}
        {passed && courseInfo && (
          <Button 
            variant="contained" 
            color="success"
            onClick={() => {
              console.log('🚀 QuizResults: Navigating to course continue with:', {
                completedFlowName: flowName,
                completedFlowId: courseInfo.flow?.id,
                courseId: courseInfo.course.id,
                fromQuiz: true
              });
              // Navigate to course continue page with state
              navigate('/course-continue', {
                state: {
                  completedFlowName: flowName,
                  completedFlowId: courseInfo.flow?.id,
                  courseId: courseInfo.course.id,
                  fromQuiz: true  // Flag to indicate this came from quiz completion
                }
              });
            }}
            size="large"
            sx={{ 
              minWidth: '160px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
            }}
          >
            🎆 Continue Course
          </Button>
        )}
        
        {/* Debug info for course button visibility */}
        {console.log('🔍 QuizResults: Button visibility check:', {
          passed,
          hasCourseInfo: !!courseInfo,
          courseInfo,
          flowName,
          shouldShowButton: passed && courseInfo
        })}
        
        {/* Fallback Continue Course button if passed but no courseInfo detected */}
        {passed && !courseInfo && flowName && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              console.log('🚀 QuizResults: Using fallback course continue for:', flowName);
              // Navigate to course continue page with minimal state
              navigate('/course-continue', {
                state: {
                  completedFlowName: flowName,
                  fromQuiz: true,
                  fallback: true  // Flag to indicate this is fallback navigation
                }
              });
            }}
            size="large"
            sx={{ 
              minWidth: '160px',
              fontSize: '1.0rem'
            }}
          >
            📚 Continue Course
          </Button>
        )}
        
        {results.can_retake && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleRetakeQuiz}
            size="large"
            sx={{ minWidth: '120px' }}
          >
            Retake Quiz
          </Button>
        )}
        
        <Button 
          variant="outlined" 
          onClick={handleBackToHome}
          size="large"
          sx={{ minWidth: '120px' }}
        >
          Back to Home
        </Button>
      </Box>
    </ResultsContainer>
  );
};

export default QuizResults;
